import type { Cue } from '@core/model/cue.ts';
import type { TrackList } from '@core/model/track.ts';
import { parseCues } from '@core/parsers/index.ts';
import type { SubtitleProvider } from '../../ports/SubtitleProvider.ts';
import { isJson3Asr, srtToWebVtt } from '../../lib/srt.ts';

type AttachedTrack = {
  value: number;
  name: string;
  code: string;
  cues: Cue[];
};

export class FileUploadSubtitleProvider implements SubtitleProvider {
  readonly id = 'upload';
  readonly label = 'Upload file';
  private readonly byVideo = new Map<string, AttachedTrack[]>();
  private nextId = 1;

  async listTracks(videoId: string): Promise<TrackList> {
    const tracks = this.byVideo.get(videoId) ?? [];
    return {
      subtitles: tracks.map(({ name, value, code }) => ({
        name,
        value,
        code,
        downloadable: true,
      })),
      translations: [],
    };
  }

  async downloadTrack(
    videoId: string,
    trackId: string,
    _opts?: { onProgress?: (message: string) => void },
  ): Promise<Cue[]> {
    void _opts;
    const id = Number(trackId);
    const tracks = this.byVideo.get(videoId) ?? [];
    const found = tracks.find((t) => t.value === id);
    if (!found) throw new Error(`Unknown upload track: ${trackId}`);
    return found.cues;
  }

  /** Parse and attach a subtitle file for a video; returns the new track value. */
  async attachFile(videoId: string, file: File, code = 'und'): Promise<number> {
    const cues = await parseSubtitleFile(file);
    const value = this.nextId++;
    const entry: AttachedTrack = {
      value,
      name: file.name || `Upload ${value}`,
      code,
      cues,
    };
    const list = this.byVideo.get(videoId) ?? [];
    list.push(entry);
    this.byVideo.set(videoId, list);
    return value;
  }

  clear(videoId?: string): void {
    if (videoId) this.byVideo.delete(videoId);
    else this.byVideo.clear();
  }
}

export async function parseSubtitleFile(file: File): Promise<Cue[]> {
  const name = file.name.toLowerCase();
  const text = await file.text();
  if (!text.trim()) {
    throw new Error('Empty subtitle file');
  }

  if (name.endsWith('.srt')) {
    const vtt = srtToWebVtt(text);
    if (!vtt.trim()) throw new Error('Invalid SRT: no cues');
    return parseCues('webvtt', vtt);
  }

  if (name.endsWith('.vtt')) {
    const vtt = srtToWebVtt(text);
    if (!vtt.trim()) throw new Error('Invalid VTT: no cues');
    return parseCues('webvtt', vtt);
  }

  if (name.endsWith('.json3') || name.endsWith('.json')) {
    let json: unknown;
    try {
      json = JSON.parse(text) as unknown;
    } catch {
      throw new Error('Invalid JSON subtitle file');
    }
    const kind = isJson3Asr(json) ? 'json3-asr' : 'json3-normal';
    return parseCues(kind, json as object);
  }

  throw new Error(`Unsupported subtitle format: ${file.name}`);
}
